import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Action, Store } from '@ngrx/store';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { filter, map, shareReplay, startWith, take, withLatestFrom } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { getRejectCandidateEmailTemplates } from '@app/collections/email-templates/email-templates.selectors';
import { getAssociationLabel } from '@collections/association-label';
import {
  getAssociationsByCandidatesIdsAndJobId,
  loadAssociationsForCandidateOnAssignToJobComponent,
} from '@collections/associations';
import { getAssociationPhase, getAssociationState } from '@modules/assigns/store';
import { AssignToJobService } from '@shared/assign-to-job/assign-to-job.service';
import { Association, AssociationLabel, EmailTemplate, Reason } from '@shared/models';
import { AssociationStatusConfiguration } from '@shared/models/association-status-configuration';
import { isTruthy } from '@shared/utils';

export interface ChangeCandidateStatusModalData {
  headerText?: string;
  batchOperation?: boolean;
  candidateId?: number;
  candidateIds?: string[];
  jobId: number;
  initialPhase?: string;
  initialState?: string;
  availableSteps: AssociationStatusConfiguration[];
  totalMatchesAssign?: boolean;
  reject?: boolean;
  actions?: Action[];
  fromDashboardLanding?: boolean;
  moveForwardOnly?: boolean;
  shouldButtonBeEnabledForOnInit?: boolean;
}

@Component({
  selector: 'mojo-change-candidate-status-modal',
  templateUrl: './change-candidate-status-modal.component.html',
  styleUrls: ['./change-candidate-status-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeCandidateStatusModalComponent implements OnInit {
  buttonDisabled = false;
  phaseFormControl: UntypedFormControl = this.fb.control(
    { value: null, disabled: this.data.reject },
    Validators.required,
  );
  stateFormControl: UntypedFormControl = this.fb.control(
    { value: null, disabled: this.data.reject },
    Validators.required,
  );
  commentFormControl: UntypedFormControl = this.fb.control(null);
  form = this.fb.group({
    phase: this.phaseFormControl,
    state: this.stateFormControl,
    comment: this.commentFormControl,
    reasons: [null],
    emailTemplateId: this.fb.control(null),
  });

  phaseOptions: AssociationStatusConfiguration[] = this.data.availableSteps;

  stateOptions$: Observable<AssociationStatusConfiguration[]> =
    this.phaseFormControl.valueChanges.pipe(
      startWith(this.data.initialPhase),
      map((selectedPhase) => {
        if (selectedPhase) {
          this.buttonDisabled =
            !this.data.shouldButtonBeEnabledForOnInit &&
            !this.data.totalMatchesAssign &&
            selectedPhase === this.data.initialPhase;
        }

        const phaseConfig =
          selectedPhase && this.phaseOptions
            ? this.phaseOptions.find(({ id }) => id === selectedPhase)
            : null;
        const children = phaseConfig ? phaseConfig.children : [];

        if (phaseConfig) {
          this.title = `Moving to ${phaseConfig.label}`;
        }

        const allowedChildren = children.filter(
          (child) => !child.blockForward || child.reasons.length,
        );
        if (allowedChildren.length > 0) {
          if (this.data.initialPhase !== selectedPhase) {
            this.stateFormControl.setValue(allowedChildren[0].id);
          } else {
            this.stateFormControl.setValue(this.data.initialState);
          }
          this.stateFormControl.setValidators([Validators.required]);
          const startIndex = allowedChildren.findIndex(({ id }) => this.data.initialState === id);
          return this.data.moveForwardOnly
            ? allowedChildren.slice(Math.max(0, startIndex))
            : allowedChildren;
        }
        this.stateFormControl.setValidators([]);
        this.stateFormControl.setValue(null);
        return null;
      }),
    );

  reasons$ = combineLatest([
    this.stateOptions$,
    this.stateFormControl.valueChanges.pipe(startWith(this.data.initialState)),
  ]).pipe(
    map(([options, selectedStateId]) => {
      if (selectedStateId) {
        this.buttonDisabled =
          !this.data.shouldButtonBeEnabledForOnInit &&
          !this.data.totalMatchesAssign &&
          selectedStateId === this.data.initialState;
      }

      const selectedState = options?.find(({ id }) => id === selectedStateId);
      const selectedPhase = this.phaseOptions?.find(({ id }) => id === this.phaseFormControl.value);

      const stateReasons = selectedState?.reasons || [];
      const phaseReasons = selectedPhase?.reasons || [];
      let reasons = stateReasons;

      if (stateReasons.length === 0) {
        reasons = phaseReasons;
      }

      return reasons;
    }),
    shareReplay(1),
  );

  phaseOptionsIds: string[] = this.data.availableSteps.map((configuration) => configuration.id);

  title = 'Change state';
  reasons: Reason[] = [];

  isDirectApplicant$: Observable<boolean>;
  enableMultiReasonSelection$ = this.featureExtensionsService.hasFeatureExtension(
    AppFeatureExtensions.ASSOCIATION_ENABLE_MULTI_REASON_SELECTION,
  );

  emailTemplateOptions$: Observable<EmailTemplate[]> = this.store.select(
    getRejectCandidateEmailTemplates,
  );
  showEmailTemplates$: Observable<boolean> = this.featureExtensionsService
    .hasFeatureExtension(AppFeatureExtensions.ASSOCIATION_EMAIL_TEMPLATES)
    .pipe(map((emailTemplatesEnabled) => !!emailTemplatesEnabled && !!this.data.reject));

  constructor(
    private dialogRef: MatDialogRef<ChangeCandidateStatusModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeCandidateStatusModalData,
    private fb: UntypedFormBuilder,
    private store: Store<{}>,
    private featureExtensionsService: MojoFeatureExtensionsService,
    private associationService: AssignToJobService,
  ) {}

  ngOnInit() {
    if (this.data.fromDashboardLanding) {
      this.store.dispatch(
        loadAssociationsForCandidateOnAssignToJobComponent({ candidateId: this.data.candidateId }),
      );
    }

    this.setInitialValues();
    if (this.data.batchOperation) {
      this.isDirectApplicant$ = of(true);
    } else if (this.data.fromDashboardLanding) {
      this.isDirectApplicant$ = of(true);
    } else {
      this.isDirectApplicant$ = this.store
        .select(getAssociationLabel, {
          candidateId: this.data.candidateId,
          jobId: this.data.jobId,
        })
        .pipe(
          withLatestFrom(
            this.featureExtensionsService.hasFeatureExtension(
              AppFeatureExtensions.ASSOCIATION_DIRECT_APPLICANT,
            ),
          ),
          map(([label, includesExtension]: [string, boolean]) =>
            includesExtension ? label === AssociationLabel.DIRECT : true,
          ),
          shareReplay(1),
        );
    }
  }

  closeDialog(actionTriggered: boolean = false, fromDashboardLanding?: boolean) {
    this.dialogRef.close({ actionTriggered, fromDashboardLanding });
  }

  onSubmit() {
    this.store
      .select(getAssociationsByCandidatesIdsAndJobId, {
        candidateIds: this.data.batchOperation ? this.data.candidateIds : [this.data.candidateId],
        jobId: this.data.jobId,
      })
      .pipe(take(1))
      .subscribe((associations) => {
        this.associationService.addAssociations(
          associations,
          this.phaseFormControl.value,
          this.stateFormControl.value,
          this.commentFormControl.value,
          this.form.controls.reasons.value,
          !!this.data.totalMatchesAssign,
          this.data.actions,
        );
        this.closeDialog(true, this.data.fromDashboardLanding);
      });
  }

  getDisabledPhaseLabel$(): Observable<string> {
    const selectedPhaseId = this.phaseFormControl.value;
    if (!!selectedPhaseId && !this.phaseOptionsIds?.includes(selectedPhaseId)) {
      return this.store
        .select(getAssociationPhase, {
          jobId: this.data.jobId,
          configurationStateId: selectedPhaseId,
        })
        .pipe(
          filter(isTruthy),
          map(({ phaseConfig }) => phaseConfig.label),
        );
    }
    return EMPTY;
  }

  getDisabledStateLabel$(stateOptions: AssociationStatusConfiguration[]): Observable<string> {
    const selectedStateId = this.stateFormControl.value;
    if (!!selectedStateId && !stateOptions.some((option) => option.id === selectedStateId)) {
      return this.store
        .select(getAssociationState, {
          jobId: this.data.jobId,
          configurationStateId: selectedStateId,
        })
        .pipe(
          filter(isTruthy),
          map((state) => state.label),
        );
    }
  }

  private setInitialValues() {
    if (this.data.initialState || this.data.initialPhase) {
      this.form.patchValue({
        state: this.data.initialState,
        phase: this.data.initialPhase,
      });
    }
  }
}
